import { Slide, TextAlignment } from '@/client/courses/types/courses.type';
import {
  setupTooltipButton,
  setupTooltipEditOnHover,
} from '@/utils/helpers/quill-tooltip';
import { setupColorPicker } from '@/utils/helpers/quill.helper';
import { Delta } from 'quill';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

type RichTextAreaProps = {
  currentEditingSlide: Slide;
  isEditingPopUpSlide: boolean;
  selectedLanguage: string;
  currentSlideIdx: number;
  currentSlideData: Slide;
  updateSlide: (id: string, updates: Partial<Slide>) => void;
  isPurpleBackground?: boolean;
};

export const RichTextArea: React.FC<RichTextAreaProps> = ({
  currentEditingSlide,
  isEditingPopUpSlide,
  selectedLanguage,
  currentSlideIdx,
  currentSlideData,
  updateSlide,
  isPurpleBackground,
}) => {
  const { t } = useTranslation();
  const quillRef = useRef<any>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setupColorPicker(quillRef);
      setupTooltipButton(quillRef);
      setupTooltipEditOnHover(quillRef);
    }, 200);

    return () => clearTimeout(timer);
  }, [currentSlideIdx, selectedLanguage, currentEditingSlide?.id]);

  useEffect(() => {
    if (quillRef.current) {
      const Quill = ReactQuill.Quill;
      const Color = Quill.import('formats/color');
      Quill.register(Color, true);
    }

    quillRef.current?.clipboard.addMatcher(
      Node.ELEMENT_NODE,
      (node: Node, delta: Delta) => {
        if (delta.ops) {
          delta.ops = delta.ops.map((op: any) => {
            return {
              insert: op.insert,
            };
          });
        }
        return delta;
      },
    );
  }, [quillRef.current]);

  return (
    <ReactQuill
      key={`${selectedLanguage}-${currentEditingSlide?.id}-${currentSlideIdx}-${
        isEditingPopUpSlide ? 'popup' : 'main'
      }`}
      value={currentEditingSlide?.html || ''}
      theme="snow"
      className={`quill-editor quill-editor-v2 ${
        currentSlideData.textAlignment === TextAlignment.CENTER
          ? 'text-center'
          : 'text-left'
      } ${isPurpleBackground ? 'quill-editor-summary' : ''}`}
      placeholder={
        isEditingPopUpSlide
          ? t('content.popup.slide.content')
          : t('content.slide.content')
      }
      formats={[
        'bold',
        'italic',
        'underline',
        'list',
        'bullet',
        'indent',
        'link',
        'color',
        'background',
        'tooltip',
      ]}
      modules={{
        toolbar: { container: '#quill-toolbar' },
        clipboard: {
          matchVisual: false,
        },
      }}
      onChange={(html, delta, source, editor) => {
        if (!currentEditingSlide?.id) return;
        if (source !== 'user' && source !== 'api') return;

        const textContent = editor.getText().trim();
        const newHtml = textContent.length > 0 ? html : '';
        if (newHtml === (currentEditingSlide?.html ?? '')) return;

        updateSlide(currentEditingSlide.id, { html: newHtml });
      }}
      ref={(el) => {
        if (el && quillRef.current !== el.getEditor()) {
          quillRef.current = el.getEditor();
        }
      }}
    />
  );
};
